/**
 * Stale-credential detector — makes "we refreshed, but this request went out
 * with the OLD token anyway" observable.
 *
 * Why this exists (2026-08-14, R5 client half)
 * ===========================================
 * TokenManager can complete a refresh and still fail to get the new JWT onto
 * disk: `_persist` is deliberately best-effort (a SecureStore failure must not
 * turn a successful refresh into a failure the boot actors read as terminal),
 * and secureTokenStorage keeps a 5s in-memory cache plus a legacy `jwt_token`
 * mirror. Every one of those seams can hand a caller a token minted BEFORE the
 * newest successful refresh. The user then sees 401s; the logs say the refresh
 * succeeded. Nothing anywhere records the contradiction.
 *
 * The check is a comparison of two numbers we already have:
 *   - the token's own `iat` (when the backend minted it)
 *   - TokenManager.lastRefreshSuccessAt() (when we last minted a NEWER one)
 * If a token predates the last successful refresh, it is by definition not the
 * token we should be sending.
 *
 * Deliberately NOT wired into the analytics event family: that requires a
 * matching backend allowlist entry (routers/admin_analytics_v2/events.py) in
 * lockstep, which is out of scope here. Follow-up: promote to a real event once
 * the CloudWatch marker shows the shape is worth persisting.
 *
 * Contract: this function NEVER throws and NEVER awaits. It sits on the hot
 * path of every authenticated request; it must be free and invisible when
 * healthy.
 */

import { decodeJwtPayload } from '../utils/jwtExpiry';

/**
 * Every offending `iat` we have already reported.
 *
 * Dedupe is per EPISODE, not per request — deliberately. One screen load in
 * this app is ~30 authenticated round-trips, so a per-call emit would turn a
 * single stale-token episode into a 30-line log storm and bury the signal it
 * exists to surface. Keyed on `iat` because that IS the identity of the
 * offending credential: the same stale token re-read 30 times is one event,
 * and the moment a genuinely different stale token appears it reports again.
 */
const _reportedStaleIats = new Set<string>();

/**
 * Note that a credential is about to be used, and warn if it predates the
 * newest refresh we know about.
 *
 * @param jwt   The Cedar JWT that is about to be read/sent.
 * @param site  Where it came from, so a firing can be traced without a stack:
 *              'storage_read'  — the hand-reader population (getCedarJwt)
 *              'fetchWithAuth' — low volume, high value: a nonzero count here
 *                                means persist failures are being swallowed on
 *                                the canonical path.
 */
export function noteTokenIssued(jwt: string, site: string): void {
  try {
    if (typeof jwt !== 'string' || !jwt) return;

    // Lazy require, NOT a static import: secureTokenStorage imports this
    // module, and tokenManager imports secureTokenStorage. A top-level import
    // of tokenManager here would close that ring into a module cycle.
    const { getTokenManager } = require('./tokenManager');
    // Compare SERVER-minted identity to SERVER-minted identity.
    //
    // The obvious predicate — this token's `iat` vs `lastRefreshSuccessAt()` —
    // is wrong in a way that makes the detector useless: `iat` is floored to
    // whole seconds by the server, while the wall clock is stamped AFTER the
    // round-trip, the JSON parse and the awaited persist. The freshly-minted
    // token therefore reads as stale on its very next use, on every healthy
    // device — a ~100% false-positive rate on a detector built to serve a live
    // credential investigation. Both operands here come from the same clock at
    // the same resolution, so a token compares equal to itself.
    const lastRefreshIat: number = getTokenManager().lastRefreshIat();

    // 0 = never refreshed this session. Nothing to be stale relative to.
    if (!lastRefreshIat || lastRefreshIat <= 0) return;

    const payload = decodeJwtPayload(jwt);
    if (!payload) return;
    const rawIat = (payload as Record<string, unknown>).iat;
    const iat = typeof rawIat === 'string' ? parseInt(rawIat, 10) : rawIat;
    if (typeof iat !== 'number' || !Number.isFinite(iat)) return;

    if (iat >= lastRefreshIat) return; // fresh — the overwhelmingly common case

    // Dedupe per (site, iat), not per iat alone.
    //
    // getCedarJwt always claims a given token first, so an iat-only key let the
    // storage_read site permanently suppress the fetchWithAuth one — silently
    // mislabelling a swallowed-persist failure on the canonical path as an
    // ordinary hand-reader. There are only two sites, so the worst case is two
    // lines per offending credential instead of one, and the per-episode intent
    // (no 30-line screen-load storm) still holds.
    const key = `${site}:${iat}`;
    if (_reportedStaleIats.has(key)) return;
    _reportedStaleIats.add(key);

    console.warn('STALE_CREDENTIAL_USED', {
      site,
      staleness_ms: (lastRefreshIat - iat) * 1000,
      iat,
    });
  } catch {
    // Observability must never be able to break auth. Silence is correct here:
    // a detector that throws on the request path would be strictly worse than
    // the blindness it was built to fix.
  }
}

/**
 * Test-only: forget which stale credentials have been reported, so each test
 * starts from a clean episode ledger. NEVER call this from production code.
 */
export function _resetStaleCredentialDetectorForTests(): void {
  _reportedStaleIats.clear();
}
